import { useToast } from '@foxtaibox/foxtailbox-frontend-core';
import { Trial_Question_Status } from '@generated/graphql';
import { Button, ButtonProps, Grid2, Tooltip } from '@mui/material';
import { useDialog } from 'context/dialogContext';
import { useUnit } from 'effector-react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { $assessmentInfo, $questionIndex, $questions } from 'store/assessment/model';
import { $questionTimer } from 'store/assessment/questionTimer';
import { QuestionPageContext } from '../context';

interface SkipDelayControlsProps {
  skipButtonProps?: ButtonProps;
  delayButtonProps?: ButtonProps;
}

const SkipDelayControls = ({ skipButtonProps, delayButtonProps }: SkipDelayControlsProps) => {
  const {
    disabled: skipButtonDisabled,
    loading: skipButtonLoading,
    ...restSkipButtonProps
  } = skipButtonProps || {};
  const {
    disabled: delayButtonDisabled,
    loading: delayButtonLoading,
    ...restDelayButtonProps
  } = delayButtonProps || {};

  const {
    delayQuestion,
    skipQuestion,
    isDelayLoading,
    setIsDelayLoading,
    isSkipLoading,
    setIsSkipLoading,
    delayQuestionLoading,
    skipQuestionLoading,
    handleFinishQuestion,
    handleFinishTesting,
    isLastQuestion,
  } = useContext(QuestionPageContext);

  const { t } = useTranslation();
  const { showDialog, closeDialog } = useDialog();
  const { showErrorToast } = useToast();
  const [questionIndex, assessmentInfo, questionTimer, questions] = useUnit([
    $questionIndex,
    $assessmentInfo,
    $questionTimer,
    $questions,
  ]);

  const handleSkipQuestion = () => {
    showDialog({
      title: t('assessment.question.skipQuestionConfirm.title'),
      content: t('assessment.question.skipQuestionConfirm.content'),
      cancelText: t('cancel'),
      confirmText: t('assessment.question.skipQuestionConfirm.confirm'),
      onConfirm: async () => {
        setIsSkipLoading(true);
        closeDialog();

        const { questionId } = questions[questionIndex];

        if (questionId) {
          skipQuestion?.({
            variables: {
              input: {
                questionId,
                trialId: assessmentInfo!.id,
              },
            },
          })
            .then(() => {
              if (isLastQuestion) {
                handleFinishTesting();
              } else {
                handleFinishQuestion?.()?.finally(() => {
                  setIsSkipLoading(false);
                });
              }
            })
            .catch(() => {
              setIsSkipLoading(false);
              showErrorToast(t('errors.common'));
            });
        }
      },
    });
  };

  const handleDelayQuestion = () => {
    showDialog({
      title: t('assessment.question.delayQuestionConfirm.title'),
      content: t('assessment.question.delayQuestionConfirm.content'),
      cancelText: t('cancel'),
      confirmText: t('assessment.question.delayQuestionConfirm.confirm'),
      onConfirm: async () => {
        setIsDelayLoading(true);
        closeDialog();

        const { questionId } = questions[questionIndex];

        if (questionId) {
          delayQuestion?.({
            variables: {
              input: {
                questionId,
                trialId: assessmentInfo!.id,
                candidateTime: questionTimer,
              },
            },
          })
            .then(() => {
              handleFinishQuestion?.()?.finally(() => {
                setIsDelayLoading(false);
              });
            })
            .catch(() => {
              setIsDelayLoading(false);
              showErrorToast(t('errors.common'));
            });
        }
      },
    });
  };

  const isQuestionDelayed = questions[questionIndex].status === Trial_Question_Status.Delayed;

  return (
    <Grid2 container spacing={2} sx={{ mt: 2 }}>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Button
          fullWidth
          size='large'
          disabled={delayQuestionLoading || isDelayLoading || skipButtonDisabled}
          loading={skipQuestionLoading || isSkipLoading || skipButtonLoading}
          variant='outlined'
          color='warning'
          onClick={handleSkipQuestion}
          {...restSkipButtonProps}>
          {t('assessment.question.skipQuestion')}
        </Button>
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Tooltip
          title={isQuestionDelayed ? t('assessment.question.doubleDelayQuestionDisabled') : ''}>
          <span>
            <Button
              fullWidth
              size='large'
              disabled={
                isLastQuestion ||
                isQuestionDelayed ||
                skipQuestionLoading ||
                isSkipLoading ||
                delayButtonDisabled
              }
              loading={delayQuestionLoading || isDelayLoading || delayButtonLoading}
              variant='outlined'
              onClick={handleDelayQuestion}
              {...restDelayButtonProps}>
              {t('assessment.question.delayQuestion')}
            </Button>
          </span>
        </Tooltip>
      </Grid2>
    </Grid2>
  );
};

export default SkipDelayControls;
